import { DOMParser as PMDOMParser, type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';

import { parseHtmlFragment } from '@shared/helpers/html.helper';
import getNodeHtml from '../../helpers/getNodeHtml.helper';
import { applyAutoIndexPatches, type TAutoIndexPatch } from '../../tiptap/plugins/shared';
import { concatElementHtml, type TElement } from './elements';

// Same options the editor parses with, or whitespace-only text nodes differ.
const PARSE_OPTIONS = { preserveWhitespace: 'full' } as const;

/**
 * Whole-document parses, and how many of them did not map one element to one top-level node.
 *
 * The design claims "once per commit, never on the keystroke path" and "one element, one node"; these
 * make both measurable rather than asserted.
 */
export const parseStats = { count: 0, lastMs: 0, notOneToOne: 0 };

// A detached ProseMirror doc, so the editor's own numbering collectors run over the whole document, not over HTML.
export const parseWholeDoc = (schema: Schema, elements: readonly TElement[]): PMNode => {
  const started = performance.now();
  const doc = PMDOMParser.fromSchema(schema).parse(parseHtmlFragment(concatElementHtml(elements)), PARSE_OPTIONS);
  parseStats.count++;
  parseStats.lastMs = Math.round(performance.now() - started);
  return doc;
};

// The collectors take an EditorState but read only `state.doc`, so a plugin-less state is enough.
export const stateOver = (doc: PMNode): EditorState => EditorState.create({ doc });

/**
 * ProseMirror rebuilds the ancestors of a patched node and leaves siblings reference-identical, so
 * identity is an exact test for "this element changed" - which keeps untouched elements byte-stable in
 * the save payload.
 */
export type TPatched = { elements: TElement[]; doc: PMNode };

export const applyPatches = (
  schema: Schema,
  elements: readonly TElement[],
  doc: PMNode,
  patches: readonly TAutoIndexPatch[]
): TPatched => {
  if (!patches.length) return { elements: elements as TElement[], doc };

  // Renumbering is cosmetic; the element list is the document, so keep it and skip the patches.
  if (doc.childCount !== elements.length) {
    parseStats.notOneToOne++;
    return { elements: elements as TElement[], doc };
  }

  const next = applyAutoIndexPatches(stateOver(doc).tr, patches).doc;
  if (next.childCount !== doc.childCount) {
    throw new Error('[largeDocument] index patches changed the element count');
  }

  const out: TElement[] = [];
  for (let i = 0; i < next.childCount; i++) {
    const after = next.child(i);
    // Spread, not a fresh pair: a rebuilt element keeps the section it sits in.
    out.push(doc.child(i) === after ? elements[i] : { ...elements[i], html: getNodeHtml(after, schema) });
  }
  // `next` is what `out` was written from, so it is the parsed form of `out`.
  return { elements: out, doc: next };
};
